import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Card, Box, Typography, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MaleInvestigation from 'components/MaleInvestigation';
import FemaleInvestigation from 'components/FemaleInvestigation';
import { api } from '../../api';

function DonorInvestigations() {
  const { donorCode } = useParams();
  const navigate = useNavigate();

  const [donorData, setDonorData] = useState(null);
  const [investigationParams, setInvestigationParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!donorCode) return;
      setLoading(true);
      try {
        const donorRes = await api.contacts.getDonorDetails(donorCode);
        const rawDonor = donorRes?.data?.donor || donorRes?.data;
        if (rawDonor) {
          setDonorData({
            id: rawDonor.id,
            name: [rawDonor.first_name, rawDonor.middle_name, rawDonor.last_name].filter(Boolean).join(' '),
            external_code: rawDonor.external_code,
            gender: rawDonor.gender,
            profile_picture_base_url: rawDonor.profile_picture_base_url || '',
            profile_picture_path: rawDonor.profile_picture_path || '',
          });
        } else {
          Swal.fire({ icon: 'error', title: 'Not Found', text: 'Donor not found' });
        }

        const paramsRes = await api.opd.getHistoryParams();
        if (paramsRes?.data) {
          setInvestigationParams(paramsRes.data);
        }
      } catch (err) {
        console.error('Error loading donor data:', err);
        Swal.fire({ icon: 'error', title: 'Load Failed', text: err?.response?.data?.message || 'Failed to load donor data' });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [donorCode]);

  const onSaveInvestigation = async (data) => {
    setSaving(true);
    try {
      await api.opd.createContactHistory(data);
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Investigation saved successfully!', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save Failed', text: err?.response?.data?.message || 'Failed to save investigation' });
    } finally {
      setSaving(false);
    }
  };

  const onFetchContactHistory = async (contactId) => {
    try {
      const res = await api.opd.getContactHistory(contactId);
      return res?.data || null;
    } catch (err) {
      if (err?.response?.status === 404) return { contact_histories: [] };
      Swal.fire({ icon: 'error', title: 'Load Failed', text: err?.response?.data?.message || 'Failed to load investigation history' });
      throw err;
    }
  };

  const onDeleteHistory = async (historyItem) => {
    try {
      const payload = {
        contact_id: historyItem.contact_id,
        param_group_id: historyItem.param_group_id,
        param_group_member_id: historyItem.param_group_member_id,
        contact_history_id: historyItem.id,
      };
      const res = await api.opd.deleteContactHistory(payload);
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Record deleted successfully!', timer: 2000, showConfirmButton: false });
      return res?.data || null;
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: err?.response?.data?.message || 'Failed to delete record' });
      throw err;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconButton
                      onClick={() => navigate('/donor-list')}
                      sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Donor Investigations – {donorData?.name || donorCode}
                    </MDTypography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Contact Histories">
                      <IconButton
                        onClick={() => navigate(`/donor-contact-histories/${donorCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.3)',
                          width: 40,
                          height: 40,
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </MDBox>

              <MDBox p={3}>
                {donorData && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#00798E', mb: 3 }}>
                      {donorData.name} – Investigation Details
                    </Typography>
                    {donorData.gender === 'M' ? (
                      <MaleInvestigation
                        patientData={donorData}
                        investigationParams={investigationParams}
                        onSaveInvestigation={onSaveInvestigation}
                        onFetchContactHistory={onFetchContactHistory}
                        onDeleteHistory={onDeleteHistory}
                        saving={saving}
                      />
                    ) : (
                      <FemaleInvestigation
                        patientData={donorData}
                        investigationParams={investigationParams}
                        onSaveInvestigation={onSaveInvestigation}
                        onFetchContactHistory={onFetchContactHistory}
                        onDeleteHistory={onDeleteHistory}
                        saving={saving}
                      />
                    )}
                  </Box>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default DonorInvestigations;
